import { useMemo } from 'react'
import { useCurrency, useStoreContent, price } from '../shared'
import { t } from '../../../../i18n'

// Spa "treatment menu": centered, airy, serif - services grouped like a
// printed menu with dotted leaders running to the price, instead of cards.
export default function BookingCalmTemplate({ services, storeName, onSelectService }) {
  const currency = useCurrency()
  const content = useStoreContent({ storeName, title: 'Slow down. Breathe.', subtitle: 'Choose a treatment and a moment that suits you. We will take care of the rest.' })
  const groups = useMemo(() => {
    const map = new Map()
    for (const s of services) {
      const key = s.category || 'Treatments'
      if (!map.has(key)) map.set(key, [])
      map.get(key).push(s)
    }
    return [...map.entries()]
  }, [services])

  return (
    <div className="max-w-3xl mx-auto pb-16" style={{ fontFamily: "Georgia, 'Times New Roman', serif", color: '#3f3a34' }}>
      <section className="text-center py-14">
        <p className="text-[11px] tracking-[0.4em] uppercase text-[color:var(--brand,#a08a6e)]" style={{ fontFamily: 'Inter, sans-serif' }}>{content.name}</p>
        <h1 className="text-4xl md:text-6xl font-light mt-6 leading-tight">{content.heroTitle}</h1>
        <div className="flex items-center justify-center gap-3 my-7 text-[#c2ab8a]" aria-hidden="true"><span className="w-12 h-px bg-current" />❋<span className="w-12 h-px bg-current" /></div>
        <p className="text-lg text-[#6b6258] max-w-xl mx-auto leading-relaxed">{content.heroSubtitle}</p>
      </section>

      {content.heroImage && <img src={content.heroImage} alt="" className="w-full h-72 object-cover rounded-[40px] mb-16" />}

      <div className="bg-[#fbf8f3] rounded-[32px] px-6 md:px-14 py-12 border border-[#ece4d8]">
        <h2 className="text-center text-sm tracking-[0.35em] uppercase text-[color:var(--brand,#a08a6e)] mb-10" style={{ fontFamily: 'Inter, sans-serif' }}>{t("Treatment menu")}</h2>
        {groups.map(([group, items]) => (
          <section key={group} className="mb-12 last:mb-0">
            <h3 className="text-center text-2xl italic mb-8">{group}</h3>
            <ul className="space-y-8">
              {items.map(s => (
                <li key={s.id}>
                  <button onClick={() => onSelectService(s)} className="w-full text-start group">
                    <div className="flex items-baseline gap-3">
                      <span className="text-xl group-hover:text-[color:var(--brand,#8a6d45)] transition-colors">{s.name}</span>
                      <span className="flex-1 border-b border-dotted border-[#c9b9a1] translate-y-[-4px]" aria-hidden="true" />
                      <span className="text-lg whitespace-nowrap">{price(s.price, currency)}</span>
                    </div>
                    <div className="flex items-start justify-between gap-6 mt-2">
                      <p className="text-sm text-[#7d7368] leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                        {s.duration_minutes} {t("minutes")}{s.description ? t(" · {description}", { description: s.description }) : ''}
                      </p>
                      <span className="shrink-0 text-[11px] tracking-[0.2em] uppercase text-[color:var(--brand,#8a6d45)] border-b border-[#c9b9a1] group-hover:border-[color:var(--brand,#8a6d45)]" style={{ fontFamily: 'Inter, sans-serif' }}>{t("Reserve")}</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>

      <p className="text-center italic text-[#8a8076] mt-14 text-lg">{t("Soft light, quiet rooms, and an hour that is only yours.")}</p>
    </div>
  )
}
